// Public pricing. Reachable signed out, from the site header — which is also
// how App Review reads it without an account (migration 20260823000300 opens
// the reference tables to `anon`).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  featureCell, LifetimeExplainer, PricingDisclaimer, PricingTable,
} from "../components/PricingTable";
import { guessCountry, pricingCatalog, regionFor } from "../lib/entitlements";
import type { PricingCatalog } from "../lib/types";

export function Pricing() {
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [yearly, setYearly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pricingCatalog()
      .then((cat) => {
        setCatalog(cat);
        setRegion(regionFor(cat.regions, guessCountry())?.code ?? null);
      })
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  return (
    <div className="wrap">
      <h1 className="page-title">Pricing</h1>
      <p className="lede">
        Publish photos and video to Facebook, Instagram and YouTube from one place. Start free and
        upgrade when you need scheduling, video or more accounts.
      </p>

      {error && <div className="card err">{error}</div>}

      {/* Rendered at build time and again before the catalogue arrives. Google
          and Meta fetch this page without executing JavaScript, so what it says
          with no data still has to be true and useful. */}
      {!catalog || !region ? (
        <>
          <div className="card">
            <h2>Four plans</h2>
            <p>
              <strong>Free</strong> — connect one account and publish photos immediately, with a
              monthly post allowance. No card, no trial clock.
            </p>
            <p>
              <strong>Basic</strong> — publish video, schedule ahead, and connect several accounts.
            </p>
            <p>
              <strong>Pro</strong> — for daily publishing: more accounts, a longer scheduling
              window, larger uploads and more storage.
            </p>
            <p>
              <strong>Lifetime</strong> — pay once and keep it, with no renewal.
            </p>
            <p className="muted">
              Exact limits and prices for your region load in a moment, and are listed in full
              below. They are also visible inside the app under Pricing.
            </p>
          </div>
          <LifetimeExplainer />
          <PricingDisclaimer />
        </>
      ) : (
        <>
          <PricingTable
            catalog={catalog}
            region={region}
            onRegion={setRegion}
            yearly={yearly}
            onYearly={setYearly}
          />

          <div className="card">
            <h2>Compare every feature</h2>
            <div className="table-scroll">
              <table className="plans">
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    {catalog.plans.map((p) => <th scope="col" key={p.code}>{p.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {catalog.features.map((f) => (
                    <tr key={f.key}>
                      <th scope="row">
                        {f.name}
                        {!f.is_implemented && <span className="soon">coming soon</span>}
                        {f.description && <span className="muted desc">{f.description}</span>}
                      </th>
                      {catalog.plans.map((p) => (
                        <td key={p.code}>{featureCell(catalog, p.code, f.key)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <LifetimeExplainer catalog={catalog} />

          <p className="muted" style={{ marginBottom: "3rem" }}>
            Questions about a plan? <Link to="/signin">Sign in</Link> and ask from the Profile
            page, or email us — the address is in the footer.
          </p>
        </>
      )}
    </div>
  );
}
